/**
 * Per-department portfolio view.
 *
 * URL: /departments/:slug — where :slug is either the encoded portfolio
 * name (e.g. "Engineering", "Sales%20%26%20Distribution") or the
 * literal sentinel "__unassigned" for projects with no portfolio set.
 *
 * Sections:
 *   - KPI cards (Not Started / In Progress / Complete) computed from
 *     project status
 *   - Awaiting approval — requested-status projects
 *   - Project list (table) — every project in this portfolio, grouped
 *     by lifecycle stage
 *   - Optional Gantt embed (toggle button; reuses the existing
 *     GanttChart component with the same project bars + diamonds)
 *
 * Capacity intentionally left off for now per Brett's brief.
 */
import { useMemo, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Building2, Calendar as CalendarIcon, ClipboardList, Plus } from "lucide-react";
import { EditProjectDialog } from "@/components/portfolio/EditProjectDialog";
import { useCan } from "@/lib/roles";
import { motion } from "framer-motion";
import { TopBar } from "@/components/layout/TopBar";
import { SupportQueueSection } from "@/components/departments/SupportQueueSection";
import { GanttChart } from "@/components/timeline/GanttChart";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useAllMilestones } from "@/hooks/useMilestones";
import { kpiBucketsFromProjects } from "@/lib/departmentKpis";
import { formatDate } from "@/lib/format";
import { effectiveHealth, statusLabel, statusPillClass } from "@/lib/projectStatus";
import { cn } from "@/lib/cn";
import { isRock } from "@/lib/rockQuarter";
import { BriefBadge } from "@/components/ui/BriefBadge";

const UNASSIGNED_KEY = "__unassigned";

export function DepartmentPortfolio() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath =
    (location.state as { from?: string } | null)?.from ?? "/departments";

  const decodedSlug = decodeURIComponent(slug ?? "");
  const { canEdit } = useCan();
  const [addOpen, setAddOpen] = useState(false);
  const isUnassigned = decodedSlug === UNASSIGNED_KEY;
  const departmentLabel = isUnassigned ? "Unassigned" : decodedSlug;

  const { data: allProjects, isLoading } = usePortfolio(false);
  const { data: milestones } = useAllMilestones();

  const projects = useMemo(() => {
    const list = Array.isArray(allProjects) ? allProjects : [];
    return list.filter((p) => {
      // Rocks (Rock-Quarter-tagged) are initiative tasks, not standalone
      // projects — they roll up under the department's Key Initiatives,
      // not the project list. (CFO direction.)
      if (isRock(p)) return false;
      const department = (p.department ?? "").trim();
      if (isUnassigned) return !department;
      return department === decodedSlug;
    });
  }, [allProjects, decodedSlug, isUnassigned]);

  // KPI counts
  const kpis = useMemo(() => kpiBucketsFromProjects(projects), [projects]);

  // Pending approval — anything currently in the `requested` lifecycle
  // stage. Surfaced as its own section above the Schedule so the
  // department lead has a one-glance "what's on my desk for approval"
  // view without having to scroll the main project list and filter by
  // status mentally.
  const requestedProjects = useMemo(
    () =>
      projects
        .filter((p) => (p.status ?? "").toLowerCase() === "requested")
        .slice()
        .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })),
    [projects],
  );

  // Gantt toggle — off by default so the page lands fast and visual
  // clutter stays low for departments that just want the list.
  const [showGantt, setShowGantt] = useState(false);

  // Compute the Gantt range from the department's projects (same shape
  // as the Timeline page). When showGantt is off we still compute it;
  // the cost is trivial and toggling becomes instant.
  const { rangeStart, rangeEnd } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today.getFullYear(), 0, 1);
    let end = new Date(today.getFullYear() + 1, 11, 31);
    const maxHorizon = new Date(today.getFullYear() + 2, 11, 31);
    for (const p of projects) {
      const e = p.end_date ? new Date(p.end_date) : null;
      if (!e) continue;
      if (e > end && e < maxHorizon) {
        end = new Date(e.getFullYear(), e.getMonth() + 1, 1);
      }
    }
    return { rangeStart: start, rangeEnd: end };
  }, [projects]);

  return (
    <>
      <TopBar
        title={departmentLabel}
        subtitle={`${projects.length} project${projects.length === 1 ? "" : "s"} in this department`}
      >
        <button
          onClick={() => navigate(fromPath)}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Departments
        </button>
        {/* Add project here (2026-10-22): starts in this department. For a
            self-managed department it saves Approved; otherwise it is a request. */}
        {canEdit && !isUnassigned && (
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700"
            data-testid="dept-add-project"
          >
            <Plus className="h-3.5 w-3.5" />
            Add project
          </button>
        )}
      </TopBar>
      {!isUnassigned && (
        <EditProjectDialog project={null} open={addOpen} onOpenChange={setAddOpen} presetDepartment={departmentLabel} />
      )}

      <div className="space-y-6 p-8">
        {/* KPI cards */}
        <div className="grid gap-3 sm:grid-cols-3">
          <KpiCard label="Not started" value={kpis.notStarted} tone="slate" />
          <KpiCard label="In progress" value={kpis.inProgress} tone="indigo" />
          <KpiCard label="Complete" value={kpis.complete} tone="emerald" />
        </div>

        {/* This department's XS support work — approved requests that
            were triaged here. Separate from projects on purpose. */}
        {!isUnassigned && (
          <SupportQueueSection departments={[departmentLabel]} department={departmentLabel} />
        )}

        {/* Awaiting approval — requested-status projects, surfaced as
            their own section so the department lead's review queue is
            visible without scanning the full list. Read-only here: the
            go/no-go itself happens on /intake, and the header says so —
            this queue renders under one name on every surface. Only
            renders when there's at least one item; otherwise the section
            is hidden entirely (a "0 pending" empty state would just be
            noise). */}
        {requestedProjects.length > 0 && (
          <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10">
            <div className="flex items-center justify-between border-b border-amber-200 dark:border-amber-500/30 px-5 py-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-300">Awaiting approval</h2>
                <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">
                  {requestedProjects.length}
                </span>
              </div>
              <Link
                to="/intake"
                className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-300 hover:underline"
                title="Approve or decline happens on Intake"
              >
                decided on Intake →
              </Link>
            </div>
            <div className="divide-y divide-amber-100">
              {requestedProjects.map((p) => (
                <button
                  key={p.id}
                  onClick={() =>
                    navigate(`/portfolio/${p.id}`, {
                      state: { from: location.pathname + location.search },
                    })
                  }
                  className="flex w-full items-center gap-3 px-5 py-2 text-left transition-colors hover:bg-amber-100/60 dark:hover:bg-amber-500/15"
                >
                  <span className="font-mono text-[11px] font-bold text-amber-800 dark:text-amber-300 w-20 shrink-0">{p.id}</span>
                  <span className="flex-1 truncate text-xs font-medium text-ink">{p.name}</span>
                  {/* Kickstart brief signal — Received vs Not Received,
                      same pill PMO sees on the Portfolio page so the
                      department lead can tell at a glance which
                      requests are ready for an approval call. */}
                  <BriefBadge status={p.brief_status} />
                  <span className="text-[10px] tabular-nums text-ink-muted w-24 text-right">
                    {p.end_date ? formatDate(p.end_date) : "—"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Schedule (Gantt) — same projects as the table below. */}
        <div className="rounded-xl border border-line bg-surface">
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-brand-navy-500" />
              <h2 className="text-sm font-semibold text-ink-2">Schedule</h2>
              <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
                {projects.filter((p) => p.start_date && p.end_date).length} with dates
              </span>
            </div>
            <button
              onClick={() => setShowGantt((v) => !v)}
              className="rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-raised"
            >
              {showGantt ? "Hide Gantt" : "Show Gantt"}
            </button>
          </div>
          {showGantt && (
            <div className="p-3">
              <GanttChart
                projects={projects}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                zoom="weeks"
                milestones={milestones ?? []}
                showMilestones
              />
            </div>
          )}
        </div>

        {/* Project list — one flat list grouped by lifecycle
            (Requested / Approved / In Flight / Complete / On Hold). */}
        <div className="rounded-xl border border-line bg-surface">
          <div className="border-b border-line px-5 py-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-brand-navy-500" />
              <h2 className="text-sm font-semibold text-ink-2">Projects</h2>
              <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
                {projects.length}
              </span>
            </div>
          </div>
          {isLoading ? (
            <div className="p-12 text-center text-sm text-ink-muted">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="p-12 text-center text-sm text-ink-subtle">
              No projects in this department.
            </div>
          ) : (
            <ProjectTable
              projects={projects}
              onNavigate={(id) =>
                navigate(`/portfolio/${id}`, {
                  state: { from: location.pathname + location.search },
                })
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "slate" | "indigo" | "emerald";
}) {
  const toneClass =
    tone === "indigo"
      ? "bg-brand-navy-50 dark:bg-brand-navy-500/10 border-brand-navy-200 dark:border-brand-navy-500/30 text-brand-navy-700 dark:text-brand-navy-300"
      : tone === "emerald"
        ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
        : "bg-canvas border-line text-ink-2";
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("rounded-xl border p-5", toneClass)}
    >
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wider opacity-80">
        {label}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Project table — compact list with status / health / dates, grouped by
// lifecycle stage. Click row → project detail.
// ---------------------------------------------------------------------------

// Lifecycle bucketing. Each project gets a key based on its status; the
// order below is the render order of sub-headers. Anything that doesn't
// match a known status (null, unrecognized) falls into "other" at the
// bottom so it's still visible — just not silently classified.
//
// headerBg / borderColor — subtle accent so each sub-section reads as a
// distinct band. Requested gets amber (CFO needs to act), Complete picks
// up brand-navy to stay consistent with the Gantt's complete-bar
// treatment, the rest stay muted (the colors are separators, not
// semantic alerts).
const LIFECYCLE_BUCKETS: {
  key: string;
  label: string;
  match: (s: string) => boolean;
  headerBg: string;
  borderColor: string;
  dotColor: string;
}[] = [
  { key: "requested",  label: "Requested",  match: (s) => s === "requested",
    headerBg: "bg-amber-50 dark:bg-amber-500/10",       borderColor: "border-amber-300 dark:border-amber-500/40",      dotColor: "bg-amber-400" },
  { key: "approved",   label: "Approved",   match: (s) => s === "approved",
    headerBg: "bg-sky-50 dark:bg-sky-500/10",         borderColor: "border-sky-300 dark:border-sky-500/40",        dotColor: "bg-sky-400" },
  { key: "in_flight",  label: "In Flight",  match: (s) =>
    s === "functional_spec" || s === "technical_spec" ||
    s === "ready_for_development" || s === "in_progress" ||
    s === "testing_go_live",
    headerBg: "bg-emerald-50 dark:bg-emerald-500/10",     borderColor: "border-emerald-300 dark:border-emerald-500/40",    dotColor: "bg-emerald-400" },
  { key: "complete",   label: "Complete",   match: (s) => s === "complete",
    headerBg: "bg-brand-navy-50 dark:bg-brand-navy-500/10",  borderColor: "border-brand-navy-300 dark:border-brand-navy-500/40", dotColor: "bg-brand-navy-500" },
  { key: "on_hold",    label: "On Hold",    match: (s) => s === "postponed" || s === "declined",
    headerBg: "bg-raised",      borderColor: "border-line-strong",      dotColor: "bg-slate-400" },
];

function ProjectTable({
  projects,
  onNavigate,
}: {
  projects: ReturnType<typeof usePortfolio>["data"];
  onNavigate: (id: string) => void;
}) {
  const list = Array.isArray(projects) ? projects : [];

  function bucketProjectsByLifecycle(projs: typeof list) {
    type Bucket = {
      key: string; label: string; projects: typeof list;
      headerBg: string; borderColor: string; dotColor: string;
    };
    const buckets: Bucket[] = [];
    for (const b of LIFECYCLE_BUCKETS) {
      const matched = projs.filter((p) => b.match((p.status ?? "").toLowerCase().trim()));
      if (matched.length > 0) {
        buckets.push({
          key: b.key, label: b.label, projects: matched,
          headerBg: b.headerBg, borderColor: b.borderColor, dotColor: b.dotColor,
        });
      }
    }
    // Catch-all for projects with unrecognized / null status — visible
    // rather than silently dropped. Uses the most muted accent so it
    // reads as "miscellaneous" rather than another lifecycle stage.
    const known = new Set(buckets.flatMap((b) => b.projects.map((p) => p.id)));
    const other = projs.filter((p) => !known.has(p.id));
    if (other.length > 0) {
      buckets.push({
        key: "other", label: "Other", projects: other,
        headerBg: "bg-canvas", borderColor: "border-line", dotColor: "bg-muted-strong",
      });
    }
    return buckets;
  }

  const cols = "grid-cols-[80px_1fr_120px_80px_100px_110px_140px]";

  const sorted = list.slice().sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true }),
  );
  const buckets = bucketProjectsByLifecycle(sorted);
  return (
    <div className="divide-y divide-line-soft">
      <div
        className={cn(
          "grid items-center gap-3 px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle",
          cols,
        )}
      >
        <div>ID</div>
        <div>Project</div>
        <div>Status</div>
        <div>Health</div>
        <div>Rock</div>
        <div className="text-right">Progress</div>
        <div className="text-right">End date</div>
      </div>
      {buckets.map((sub, i) => (
        <div key={sub.key} className={cn(i > 0 && "mt-1")}>
          <div
            className={cn(
              "grid items-center gap-3 border-l-2 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-3",
              sub.headerBg,
              sub.borderColor,
              cols,
            )}
          >
            <span className="col-span-7 flex items-center gap-2">
              {/* Small status dot — picks up the same accent color as
                  the left border, anchoring the section's identity even
                  on monitors where the thin border is hard to read. */}
              <span className={cn("inline-block h-1.5 w-1.5 rounded-full", sub.dotColor)} />
              {sub.label}
              <span className="ml-1 rounded-full bg-surface/70 px-1.5 py-0.5 text-[9px] font-bold text-ink-3 ring-1 ring-line">
                {sub.projects.length}
              </span>
            </span>
          </div>
          {sub.projects.map((p) => renderProjectRow(p, onNavigate, cols))}
        </div>
      ))}
    </div>
  );
}

// Single source of truth for a project row in the Department portfolio
// table.
function renderProjectRow(
  p: { id: string; name: string; status: string | null; health: string | null;
       pct_complete: number; end_date: string | null;
       rock_quarter?: string | null },
  onNavigate: (id: string) => void,
  cols: string,
) {
  const rockQuarter = p.rock_quarter;
  return (
    <button
      key={p.id}
      onClick={() => onNavigate(p.id)}
      className={cn(
        "grid w-full items-center gap-3 px-5 py-2 text-left transition-colors hover:bg-raised",
        cols,
      )}
    >
      <span className="font-mono text-[11px] font-bold text-ink-3">{p.id}</span>
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-xs font-medium text-ink">{p.name}</span>
      </span>
      <span>
        {p.status ? (
          <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", statusPillClass(p.status))}>
            {statusLabel(p.status)}
          </span>
        ) : (
          <span className="text-[10px] text-ink-faint">—</span>
        )}
      </span>
      <span>
        {(() => {
          const eh = effectiveHealth(p.status, p.health);
          return (
            <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", eh.pillClass)}>
              {eh.label}
            </span>
          );
        })()}
      </span>
      <span>
        {rockQuarter ? (
          <span className="inline-flex rounded-full bg-sky-100 dark:bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30">
            {rockQuarter} Rock
          </span>
        ) : (
          <span className="text-[10px] text-ink-faint">—</span>
        )}
      </span>
      {/* % Complete — inline bar + number, right-aligned to match
          the column header. Bar flips to emerald at 100% so a
          finished project pops without needing to read the number. */}
      <span className="flex items-center justify-end gap-1.5">
        <div className="h-1.5 w-12 overflow-hidden rounded-full bg-raised">
          <div
            className={cn(
              "h-full rounded-full",
              (p.pct_complete ?? 0) >= 1
                ? "bg-emerald-500"
                : "bg-brand-navy-500",
            )}
            style={{ width: `${Math.min(100, Math.max(2, Math.round((p.pct_complete ?? 0) * 100)))}%` }}
          />
        </div>
        <span className="text-[10px] font-semibold tabular-nums text-ink-2 w-8 text-right">
          {Math.round((p.pct_complete ?? 0) * 100)}%
        </span>
      </span>
      <span className="text-right text-[11px] tabular-nums text-ink-3">
        {p.end_date ? formatDate(p.end_date) : <span className="text-ink-faint">—</span>}
      </span>
    </button>
  );
}
